import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FragmentData, gql } from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

import { Flex } from '@reckon-web/flex';
import { Heading } from '@reckon-web/heading';
import { Box } from '@reckon-web/box';
import { LoadingDots } from '@reckon-web/loading';
import { Button } from '@reckon-web/button';
import { PlusIcon } from '@reckon-web/icon';
import { DrawerController } from '@reckon-web/drawer';
import { Field } from '@reckon-web/field';
import { SearchInput } from '@reckon-web/search-input';
import { SelectMenu } from '@reckon-web/select-menu';
import { Stack } from '@reckon-web/stack';
import { Text } from '@reckon-web/text';
import { useToasts } from '@reckon-web/toast';
import { Notice } from '@reckon-web/notice';

import { useUser } from '../../auth';
import { Wrapper } from '../Wrapper';
import { useDebounce } from '../../utilities/hooks/useDebounce';

import { usersFragment } from './UsersTable';
import { UsersTable } from './UsersTable';
import { CreateEditUserDrawer } from './createEditUserDrawer/CreateEditUserDrawer';
import { Product } from './types';

const TENANT_ROLES_AND_PRODUCTS = gql`
  query TenantRolesAndProducts {
    getTenantRoleList {
      roleName
      roleDescription
    }

    getTenantProductList {
      productId
      friendlyName
      internalName
    }
  }
` as import('../../../__generated__/ts-gql/TenantRolesAndProducts').type;

const CLIENT_INTEGRATION = gql`
  query IsClientIntegrationEnabled {
    isClientIntegrationEnabled
  }
` as import('../../../__generated__/ts-gql/IsClientIntegrationEnabled').type;

const TENANT_USER_LIST_QUERY = gql`
  query TenantUserList($filter: TenantUserListFilter) {
    getTenantUserList(filter: $filter) {
      tenantUsers {
        ...TenantUsers_user
      }
      hasMoreItems
    }
  }
` as import('../../../__generated__/ts-gql/TenantUserList').type;

type User = FragmentData<typeof usersFragment>;

const filterLabels = ['Partners', 'Managers', 'Active'];

export function UsersContainer({
  manageUserId,
  setManageUserId,
}: {
  manageUserId: string | null;
  setManageUserId: Dispatch<SetStateAction<string | null>>;
}) {
  const { addToast } = useToasts();
  const [tenantUserList, setTenantUserList] = useState<User[]>([]);
  const cognitoUser = useUser();
  const { data: tenantRolesAndProducts, loading: loadRolesAndProducts } =
    useQuery(TENANT_ROLES_AND_PRODUCTS);

  const { data: clientIntegration, loading: clientIntegrationLoading } =
    useQuery(CLIENT_INTEGRATION);

  const [searchVal, setSearchVal] = useState('');
  const [filters, setFilters] = useState<typeof filterLabels>(['Active']);

  const isActiveFilter = filters.includes('Active');
  const isManagersFilter = filters.includes('Managers');
  const isPartnersFilter = filters.includes('Partners');

  const debouncedSearchVal = useDebounce(searchVal, 200).trim();

  const {
    data: tenantUserListData,
    loading: loadingUsers,
    error,
    refetch,
  } = useQuery(TENANT_USER_LIST_QUERY, {
    variables: {
      filter: {
        fullName: debouncedSearchVal,
        active: isActiveFilter || null,
        manager: isManagersFilter || null,
        partner: isPartnersFilter || null,
      },
    },
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editUser, setEditUser] = useState<FragmentData<
    typeof usersFragment
  > | null>(null);

  useEffect(() => {
    const tenantUsers: User[] =
      tenantUserListData?.getTenantUserList?.tenantUsers?.map((user) => {
        return user;
      }) || [];

    setTenantUserList(tenantUsers);
  }, [tenantUserListData]);

  useEffect(() => {
    // header manage user is clicked this will occur
    if (manageUserId && !!tenantUserList.length) {
      const findUser = tenantUserList.find(
        (user) => user.cloudId === manageUserId
      );
      if (findUser) {
        setEditUser(findUser);
        setDrawerOpen(true);
      }
      setManageUserId(null);
    }
  }, [
    setEditUser,
    tenantRolesAndProducts,
    manageUserId,
    setManageUserId,
    tenantUserList,
  ]);

  useEffect(() => {
    refetch();
  }, [isActiveFilter, isManagersFilter, isPartnersFilter, refetch]);

  const hasMoreUsers = useMemo(() => {
    return !!tenantUserListData?.getTenantUserList?.hasMoreItems;
  }, [tenantUserListData?.getTenantUserList?.hasMoreItems]);

  const handleRefetch = async () => {
    try {
      await refetch();
    } catch (err: any) {
      addToast({
        title: 'Something went wrong',
        tone: 'critical',
      });
    }
  };

  const tenantUsers = useMemo(() => {
    return [...tenantUserList].map((i) => {
      // TEMP order products list on the frontend
      const productsCopy = (i.products && [...i.products]) || [];
      const sortProducts = productsCopy.sort(compare);
      return { ...i, products: sortProducts };
    });
  }, [tenantUserList]);

  const rootCanDeactivate = tenantUserList?.find(
    (user) =>
      user.roleName === 'root' &&
      user.active &&
      cognitoUser?.profile?.staffId !== user?.cloudId
  );

  const selectedCount = filters.length;
  const triggerSuffix =
    selectedCount > 2
      ? `${filters[0]} +${selectedCount - 1} more`
      : filters.join(', ');
  const triggerLabel = 'Filters' + (triggerSuffix ? ': ' : '') + triggerSuffix;

  const productList =
    (tenantRolesAndProducts?.getTenantProductList as readonly Product[]) || [];

  const canEditUser = !!clientIntegration?.isClientIntegrationEnabled;

  return (
    <Wrapper>
      <Box marginBottom="xxlarge">
        {!clientIntegrationLoading && !canEditUser && (
          <Notice>
            Client integration is still being configured. Users cannot be edited
            until configuration is complete.
          </Notice>
        )}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginTop="medium"
        >
          <Box marginX="xsmall">
            <Heading level="1">Users</Heading>
          </Box>
          <Flex alignItems="center">
            <Field label="Search users" labelVisible={false}>
              <SearchInput
                placeholder="Search users.."
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                onClear={() => setSearchVal('')}
                size="small"
              />
            </Field>

            <Box marginX="large">
              <SelectMenu
                trigger={triggerLabel}
                onChange={(selected) => {
                  if (filters.includes(selected)) {
                    setFilters((old) => old.filter((opt) => opt !== selected));
                  } else {
                    setFilters((old) => old.concat(selected));
                  }
                }}
                options={filterLabels}
                itemToLabel={(i) => i}
                itemToValue={(i) => i}
                value={filters}
              />
            </Box>

            <Button
              disabled={!canEditUser}
              iconBefore={PlusIcon}
              label="New user"
              onClick={() => setDrawerOpen(true)}
              size="small"
            />
          </Flex>
        </Flex>
        <DrawerController isOpen={drawerOpen}>
          <CreateEditUserDrawer
            setDrawerOpen={setDrawerOpen}
            editUser={editUser}
            setEditUser={setEditUser}
            canEditUser={canEditUser}
            roleList={tenantRolesAndProducts?.getTenantRoleList || []}
            productList={productList}
          />
        </DrawerController>
        {loadingUsers && (
          <Flex
            width="100%"
            height="60vh"
            alignItems="center"
            justifyContent="center"
          >
            <LoadingDots label="loading..." size="large" />
          </Flex>
        )}
        {!loadingUsers && !loadRolesAndProducts && !!error && (
          <Flex
            direction="column"
            gap="large"
            width="100%"
            height="60vh"
            alignItems="center"
            justifyContent="center"
          >
            <Heading level="2">Something went wrong!</Heading>
            <Text>Please try again</Text>
            <Button label="Try again" onClick={handleRefetch} />
          </Flex>
        )}
        {!!tenantUsers &&
          !loadingUsers &&
          tenantRolesAndProducts?.getTenantRoleList && (
            <Stack gap="large">
              <UsersTable
                tenantUsers={tenantUsers}
                setDrawerOpen={setDrawerOpen}
                setEditUser={setEditUser}
                rootCanDeactivate={!!rootCanDeactivate}
                // @ts-ignore will fix later
                roleList={tenantRolesAndProducts?.getTenantRoleList}
                isFiltersApplied={!!filters.length}
                searchVal={debouncedSearchVal}
                productList={productList}
                canEditUser={canEditUser}
              />
              {(hasMoreUsers && !!tenantUsers.length && (
                <>
                  {(!debouncedSearchVal && (
                    <Stack alignSelf="center" align="center" gap="medium">
                      <Text color="muted">
                        Showing {tenantUsers.length} results of many.
                      </Text>
                      <Text color="muted">
                        If the user you’re looking for is not displayed, try to
                        search their name or adjusting filters. Note that
                        searching users is case sensitive.
                      </Text>
                    </Stack>
                  )) || (
                    <Stack alignSelf="center" align="center" gap="medium">
                      <Text color="muted">
                        Showing {tenantUsers.length} results of many for{' '}
                        <Text inline weight="semibold">
                          "{debouncedSearchVal}"
                        </Text>
                        .
                      </Text>
                      <Text color="muted">
                        If the user you’re looking for is not displayed, try to
                        search their name or adjusting filters. Note that
                        searching users is case sensitive.
                      </Text>
                    </Stack>
                  )}
                </>
              )) ||
                null}
            </Stack>
          )}
      </Box>
    </Wrapper>
  );
}

function compare(a: any, b: any) {
  if (a.friendlyName < b.friendlyName) {
    return -1;
  }
  if (a.friendlyName > b.friendlyName) {
    return 1;
  }
  return 0;
}
